import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { connectionApi, webhookApi, postApi } from '@/lib/api';
import { queryKeys } from './shared';

// Connections
export const useConnections = (workspaceId: string) =>
  useQuery({
    queryKey: queryKeys.connections(workspaceId),
    queryFn: () => connectionApi.list(workspaceId),
    enabled: !!workspaceId,
  });

/** All connections across brand + workspace level — used by multi-account
 *  pickers (brand default posting accounts, review inbox "Post as" dropdown). */
export const useAllConnections = (workspaceId: string) =>
  useQuery({
    queryKey: ['connections-all', workspaceId],
    queryFn: () => connectionApi.listAll(workspaceId),
    enabled: !!workspaceId,
    staleTime: 60_000,
  });

/**
 * Set of platforms publishable for a given brand. Mirrors the backend
 * resolver: a platform is usable if there's a brand-level connection
 * for it, OR a workspace-level (brand_id null) fallback. Instagram
 * piggybacks on Facebook (Meta share the same Graph token), so an FB
 * connection unlocks IG too. Used by Write/Generate modals to grey
 * out unconnected pills before the backend 422s the request.
 *
 * Returns null while connections are loading so callers can decide
 * whether to render disabled-everywhere pills or wait.
 */
export const useBrandConnectedPlatforms = (
  workspaceId: string,
  brandId: string | undefined,
): Set<string> | null => {
  const { data, isPending } = useAllConnections(workspaceId);
  if (isPending || !data || !brandId) return null;
  const platforms = new Set<string>();
  for (const c of data) {
    if (!c.connected) continue;
    if (c.brandId === brandId || c.brandId === null) {
      platforms.add(c.platform);
    }
  }
  // FB→IG flip: if Facebook is connected, Instagram is publishable too.
  if (platforms.has('FACEBOOK')) platforms.add('INSTAGRAM');
  return platforms;
};

/** Per-workspace webhook health (Phase 12.1). Two queries — one per
 *  platform — so a Meta-side outage can't blank the X badges and
 *  vice versa. Refetch every 30s while the page is open. */
export const useMetaWebhookStatus = (workspaceId: string) =>
  useQuery({
    queryKey: ['webhook-status', 'meta', workspaceId],
    queryFn: () => webhookApi.metaStatus(workspaceId),
    enabled: !!workspaceId,
    staleTime: 30_000,
  });

export const useXWebhookStatus = (workspaceId: string) =>
  useQuery({
    queryKey: ['webhook-status', 'x', workspaceId],
    queryFn: () => webhookApi.xStatus(workspaceId),
    enabled: !!workspaceId,
    staleTime: 30_000,
  });

export const useBrandConnections = (workspaceId: string, brandId: string) =>
  useQuery({
    queryKey: queryKeys.brandConnections(workspaceId, brandId),
    queryFn: () => connectionApi.listBrand(workspaceId, brandId),
    enabled: !!workspaceId && !!brandId,
  });

export const useDisconnectBrandPlatform = (workspaceId: string, brandId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (platform: string) => connectionApi.disconnectBrand(workspaceId, brandId, platform),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.brandConnections(workspaceId, brandId) });
    },
  });
};

export const useDisconnectPlatform = (workspaceId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (platform: string) => connectionApi.disconnect(workspaceId, platform),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.connections(workspaceId) });
    },
  });
};

export const useDashboardStats = (workspaceId: string) =>
  useQuery({
    queryKey: queryKeys.stats(workspaceId),
    queryFn: () => postApi.stats(workspaceId),
    enabled: !!workspaceId,
  });
